import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ScanLine, Camera, CameraOff, Keyboard, Loader2, PackageCheck, AlertTriangle, CheckCircle2,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import CompletePackageForm from "@/components/CompletePackageForm";
import ScannedPackageDetail from "@/components/ScannedPackageDetail";

export default function Scanner() {
  const { user } = useAuth();
  const [cameraOn, setCameraOn] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [manualCode, setManualCode] = useState("");
  const [scanResult, setScanResult] = useState(null); // {qr, package, complete, missing}
  const [recent, setRecent] = useState([]);
  const scannerRef = useRef(null);

  useEffect(() => {
    refreshRecent();
    return () => {
      stopCamera();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshRecent = async () => {
    try {
      const { data } = await api.get("/dispatcher/history");
      setRecent(data.slice(0, 5));
    } catch (e) { console.debug("refreshRecent failed:", e); }
  };

  const stopCamera = async () => {
    if (scannerRef.current) {
      try { await scannerRef.current.stop(); } catch (e) { console.debug("scanner.stop ignored:", e); }
      try { scannerRef.current.clear(); } catch (e) { console.debug("scanner.clear ignored:", e); }
      scannerRef.current = null;
    }
    setCameraOn(false);
  };

  const startCamera = async () => {
    try {
      setCameraOn(true);
      const qr = new Html5Qrcode("qr-reader");
      scannerRef.current = qr;
      await qr.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 240, height: 240 } },
        async (decoded) => {
          await stopCamera();
          await submitCode(decoded);
        },
        () => {}
      );
    } catch (err) {
      toast.error("Camera access denied. Use manual entry below.");
      setCameraOn(false);
    }
  };

  const submitCode = async (code) => {
    setScanning(true);
    try {
      const { data } = await api.post("/dispatcher/scan", { code });
      setScanResult(data);
      refreshRecent();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setScanning(false);
    }
  };

  const onManualSubmit = (e) => {
    e.preventDefault();
    if (!manualCode.trim()) return;
    submitCode(manualCode.trim());
    setManualCode("");
  };

  const onMarkPickup = async () => {
    if (!scanResult?.qr?.qr_code_id) return;
    try {
      await api.post(`/dispatcher/qr/${scanResult.qr.qr_code_id}/mark-for-pickup`);
      toast.success("Package has been marked as For Pick-Up.");
      setScanResult(null);
      refreshRecent();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="scanner-page" className="space-y-5">
      <div>
        <div className="text-xs font-mono-tech uppercase tracking-[0.2em] text-cyan-400 mb-1">/ Scan QR</div>
        <h1 className="font-display text-2xl font-bold tracking-tight">Hey, {user?.name?.split(" ")[0] || "Dispatcher"}</h1>
        <p className="text-slate-600 dark:text-slate-400 text-sm">Scan a package QR to process it.</p>
      </div>

      {/* Scanner viewport */}
      <div className="rounded-xl overflow-hidden border border-slate-200 dark:border-slate-800 bg-slate-100/60 dark:bg-slate-900/60 relative">
        <div id="qr-reader" className={cameraOn ? "w-full" : "hidden"} />
        {!cameraOn && (
          <div className="aspect-square grid place-items-center text-center p-6">
            <div>
              <div className="w-20 h-20 rounded-full bg-cyan-500/10 border border-cyan-500/30 grid place-items-center mx-auto mb-4">
                <ScanLine className="w-9 h-9 text-cyan-400" />
              </div>
              <div className="font-display text-lg font-semibold mb-1">Tap to start scanning</div>
              <div className="text-xs text-slate-600 dark:text-slate-400 mb-5">Point your phone at the QR code on the package label.</div>
              <Button
                data-testid="start-camera-btn"
                onClick={startCamera}
                disabled={scanning}
                className="bg-cyan-400 text-slate-950 hover:bg-cyan-300 font-medium h-12 px-6"
              >
                <Camera className="w-4 h-4 mr-2" /> Open Camera
              </Button>
            </div>
          </div>
        )}
        {cameraOn && (
          <button
            data-testid="stop-camera-btn"
            onClick={stopCamera}
            className="absolute top-3 right-3 bg-white/70 dark:bg-slate-950/70 border border-slate-300 dark:border-slate-700 rounded-full px-3 py-1.5 text-xs flex items-center gap-1.5 hover:bg-slate-100 hover:dark:bg-slate-900"
          >
            <CameraOff className="w-3.5 h-3.5" /> Stop
          </button>
        )}
        {scanning && (
          <div className="absolute inset-0 grid place-items-center bg-black/70 backdrop-blur-sm">
            <div className="text-center">
              <Loader2 className="w-8 h-8 animate-spin text-cyan-400 mx-auto mb-2" />
              <div className="text-sm">Looking up…</div>
            </div>
          </div>
        )}
      </div>

      {/* Manual entry */}
      <form onSubmit={onManualSubmit} className="border border-slate-200 dark:border-slate-800 rounded-xl bg-slate-100/40 dark:bg-slate-900/40 p-4">
        <div className="flex items-center gap-2 mb-2 text-slate-600 dark:text-slate-400">
          <Keyboard className="w-4 h-4" />
          <div className="text-xs font-mono-tech uppercase tracking-[0.2em]">Manual entry</div>
        </div>
        <div className="flex gap-2">
          <Input
            data-testid="manual-code-input"
            value={manualCode}
            onChange={e => setManualCode(e.target.value)}
            placeholder="QR-XXXXXXXX"
            className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 font-mono-tech uppercase"
          />
          <Button
            type="submit"
            data-testid="manual-submit-btn"
            disabled={scanning || !manualCode.trim()}
            className="bg-cyan-400 text-slate-950 hover:bg-cyan-300 font-medium"
          >
            Look up
          </Button>
        </div>
      </form>

      {/* Recent scans */}
      {recent.length > 0 && (
        <div>
          <div className="text-xs font-mono-tech uppercase tracking-[0.2em] text-slate-500 mb-2">/ Recent</div>
          <div className="space-y-2">
            {recent.map(r => (
              <div
                key={r.id}
                data-testid={`recent-${r.id}`}
                className="border border-slate-200 dark:border-slate-800 rounded-md bg-slate-100/40 dark:bg-slate-900/40 p-3 flex items-center justify-between gap-2"
              >
                <div className="min-w-0">
                  <div className="font-mono-tech text-xs text-cyan-300 truncate">{r.qr_code_id || "—"}</div>
                  <div className="text-xs text-slate-600 dark:text-slate-400 truncate">{prettifyAction(r.action_taken)}</div>
                </div>
                <div className="text-[10px] text-slate-500 font-mono-tech text-right shrink-0">
                  {new Date(r.created_at).toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Result dialog */}
      <Dialog open={!!scanResult} onOpenChange={(o) => !o && setScanResult(null)}>
        <DialogContent className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white max-w-lg max-h-[90vh] overflow-y-auto scrollbar-thin">
          {scanResult && scanResult.complete ? (
            <CompletePath
              result={scanResult}
              onCancel={() => setScanResult(null)}
              onMarkPickup={onMarkPickup}
            />
          ) : (
            scanResult && (
              <IncompletePath
                result={scanResult}
                onClose={() => setScanResult(null)}
                onSaved={async (markPickup) => {
                  if (markPickup) {
                    try {
                      await api.post(`/dispatcher/qr/${scanResult.qr.qr_code_id}/mark-for-pickup`);
                      toast.success("Package has been marked as For Pick-Up.");
                    } catch (e) {
                      toast.error(formatApiError(e));
                    }
                  }
                  setScanResult(null);
                  refreshRecent();
                }}
              />
            )
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function prettifyAction(a) {
  if (!a) return "—";
  return a.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
}

function CompletePath({ result, onCancel, onMarkPickup }) {
  return (
    <>
      <DialogHeader>
        <DialogTitle className="font-display flex items-center gap-2">
          <CheckCircle2 className="w-5 h-5 text-green-400" /> Package Ready
        </DialogTitle>
        <DialogDescription className="text-slate-600 dark:text-slate-400 font-mono-tech text-xs">
          {result.qr.qr_code_id} · {result.package?.package_id}
        </DialogDescription>
      </DialogHeader>
      <ScannedPackageDetail qr={result.qr} pkg={result.package} />
      <div className="grid grid-cols-2 gap-2 mt-4 sticky bottom-0 bg-white dark:bg-slate-950 pt-2">
        <Button
          data-testid="scan-cancel-btn"
          variant="outline"
          onClick={onCancel}
          className="h-12 bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white hover:bg-slate-200 hover:dark:bg-slate-800 hover:text-slate-900 hover:dark:text-white"
        >
          Cancel
        </Button>
        <Button
          data-testid="scan-for-pickup-btn"
          onClick={onMarkPickup}
          className="h-12 bg-cyan-400 text-slate-950 hover:bg-cyan-300 font-bold uppercase tracking-wider"
        >
          <PackageCheck className="w-5 h-5 mr-2" /> For Pick-Up
        </Button>
      </div>
    </>
  );
}

function IncompletePath({ result, onClose, onSaved }) {
  const [showForm, setShowForm] = useState(false);
  if (showForm) {
    return (
      <CompletePackageForm
        qr={result.qr}
        pkg={result.package}
        onCancel={() => setShowForm(false)}
        onSaved={onSaved}
      />
    );
  }
  return (
    <>
      <DialogHeader>
        <DialogTitle className="font-display flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-yellow-400" /> Complete Package Information
        </DialogTitle>
        <DialogDescription className="text-slate-600 dark:text-slate-400">
          This QR code does not have complete package information yet. Please complete the required details before marking it For Pick-Up.
        </DialogDescription>
      </DialogHeader>
      <div className="my-3 p-3 rounded-md border border-yellow-500/20 bg-yellow-500/5 text-xs text-yellow-100">
        <div className="font-mono-tech uppercase tracking-wider text-yellow-300 mb-1">Missing</div>
        <ul className="space-y-0.5 list-disc list-inside">
          {(result.missing || []).map((m, i) => <li key={i}>{m.replace(/_/g, " ")}</li>)}
        </ul>
      </div>
      <div className="grid grid-cols-2 gap-2 mt-2">
        <Button
          data-testid="incomplete-cancel-btn"
          variant="outline"
          onClick={onClose}
          className="h-12 bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white hover:bg-slate-200 hover:dark:bg-slate-800 hover:text-slate-900 hover:dark:text-white"
        >
          Cancel
        </Button>
        <Button
          data-testid="incomplete-complete-btn"
          onClick={() => setShowForm(true)}
          className="h-12 bg-cyan-400 text-slate-950 hover:bg-cyan-300 font-bold"
        >
          Complete Now
        </Button>
      </div>
    </>
  );
}
